import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";

/** Wire types for `/v1/hq/trust-safety/desk/*` (backend `trust-safety/desk.ts`). */

export interface Person {
  id: string;
  display_name: string | null;
  handle: string | null;
  avatar: string | null;
  account_status: string | null;
}

export type SlaState = "ok" | "due_soon" | "breached" | "none";

export interface DeskReport {
  id: string;
  /** i105: who is working it; null = shared queue. */
  assigned_to_user_id?: string | null;
  submitted_at: string;
  status: string;
  priority: string;
  sla_due_at: string | null;
  sla_state: SlaState;
  reason_code: string | null;
  reason_label: string | null;
  source: string;
  report_type: string;
  subject_type: string;
  subject_id: string;
  subject_preview: { text: string | null; has_image: boolean; removed: boolean };
  evidence_reference: string | null;
  reporter: Person | null;
  reported_user: Person | null;
  reports_on_same_subject: number;
  prior_reports_against_user: number;
  moderation_case_id: string | null;
  resolution: string | null;
  resolved_at: string | null;
}

export interface SubjectMedia {
  kind: "image" | "video";
  url: string | null;
  thumbnail_url: string | null;
  private: boolean;
  expires_at: string | null;
  note: string | null;
}

export interface SubjectView {
  subject_type: string;
  subject_id: string;
  exists: boolean;
  removable: boolean;
  removed: { removal_id: string | null; removed_at: string; restored: boolean } | null;
  owner: Person | null;
  title: string | null;
  text: string | null;
  posted_at: string | null;
  where: { community_id: string | null; community_name: string | null; surface: string } | null;
  media: SubjectMedia[];
  context: Array<{ kind: string; id: string; author: string | null; text: string | null; posted_at: string | null }>;
  facts: Record<string, string | number | boolean | null>;
  hq_path: string | null;
}

export interface Takedown {
  id: string;
  subject_type: string;
  subject_id: string;
  owner_user_id: string | null;
  report_id: string | null;
  moderation_case_id: string | null;
  reason_code: string;
  reason_label?: string;
  reason: string | null;
  media_refs: Array<{ purpose: string; key: string; label: string }>;
  removed_at: string;
  restored_at: string | null;
  restore_reason: string | null;
  media_purged_at: string | null;
  owner?: Person | null;
  removed_by?: Person | null;
  restored_by?: Person | null;
  has_purgeable_media?: boolean;
  preview?: { text: string | null; exists: boolean; has_media: boolean } | null;
}

export interface UserHistory {
  user_id: string;
  reports_total: number;
  reports_last_30d: number;
  reports_actioned: number;
  reports_dismissed: number;
  reports_open: number;
  takedowns_total: number;
  enforcements: Array<Record<string, unknown>>;
  takedowns: Array<Record<string, unknown>>;
  current_restriction: string | null;
}

export interface ReportContext {
  report: DeskReport & { subject_snapshot: Record<string, unknown> | null };
  reporter_track_record: { total: number; dismissed: number } | null;
  subject: SubjectView;
  reported_user: Person | null;
  other_reports: Array<{
    id: string;
    reporter: Person | null;
    reason_code: string | null;
    reason_label: string | null;
    submitted_at: string;
    status: string;
    resolution: string | null;
    source: string;
    note: string | null;
  }>;
  takedowns: Array<Takedown & { removed_by: Person | null }>;
  moderation_case: Record<string, unknown> | null;
  enforcements: Array<Record<string, unknown>>;
  appeals: Array<Record<string, unknown>>;
  internal_notes: Array<{ id: string; body: string; created_at: string; author: Person | null }>;
  history: UserHistory | null;
  audit_trail: Array<{ id: string; action: string; occurred_at: string; reason: string | null; target_type: string; actor: Person | null }>;
}

export interface Overview {
  open_reports: number;
  open_by_priority: Record<string, number>;
  sla_breached: number;
  sla_due_soon: number;
  escalated_reports: number;
  auto_flagged_open: number;
  open_cases: number;
  unowned_cases: number;
  proposed_enforcements: number;
  active_enforcements: number;
  open_appeals: number;
  takedowns_active: number;
  takedowns_last_7d: number;
  blocklist_active_terms: number;
  blocklist_hits_total: number;
  reports_last_30d: number;
  actioned_last_30d: number;
  dismissed_last_30d: number;
  avg_hours_to_resolve_30d: number | null;
  reasons_last_30d: Record<string, number>;
  repeat_subjects: Array<{ user: Person; reports_last_30d: number; restriction: string | null }>;
}

export interface BlocklistTerm {
  id: string;
  term: string;
  match_type: "contains" | "word";
  surfaces: string[];
  priority: string;
  is_active: boolean;
  note: string | null;
  hit_count: number;
  last_hit_at: string | null;
  created_at: string;
  created_by: Person | null;
}

export type DecisionAction =
  | "remove_content"
  | "warn"
  | "restrict_user"
  | "suspend_user"
  | "ban_user"
  | "remove_community";

export interface DecisionBody {
  outcome: "dismiss" | "action" | "escalate";
  actions: DecisionAction[];
  reason_code?: string;
  note: string;
  message_to_user?: string;
  /** Suspension: 1 to 30 days. */
  duration_days?: number;
  /** Restriction: 1 to 48 hours. */
  duration_hours?: number;
  /** A support ticket this also answers (kept on the record). */
  ticket_id?: string;
  resolve_related: boolean;
}

export const RESTRICT_HOUR_PRESETS = [1, 6, 12, 24, 48] as const;
export const SUSPEND_DAY_PRESETS = [1, 3, 7, 30] as const;

export interface CommunityRemoval {
  id: string;
  club: { id: string; name: string | null; handle: string | null };
  subject_type: "post" | "comment" | "chat_message";
  subject_id: string;
  author: Person | null;
  removed_by: Person | null;
  removed_by_role: string;
  removed_by_count_30d: number;
  reason: string | null;
  snapshot: Record<string, unknown> | null;
  removed_at: string;
}

const BASE = `${API_VERSION_PREFIX}/hq/trust-safety/desk`;

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") continue;
    search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

export function createDeskApi(client: ApiClient) {
  return {
    overview: () => client.get<{ overview: Overview }>(`${BASE}/overview`),
    listReports: (params: {
      view?: string;
      priority?: string;
      reason_code?: string;
      subject_type?: string;
      source?: string;
      q?: string;
      limit?: number;
      offset?: number;
    }) =>
      client.get<{ reports: DeskReport[]; total: number; limit: number; offset: number }>(
        `${BASE}/reports${qs(params)}`,
      ),
    reportContext: (id: string) => client.get<ReportContext>(`${BASE}/reports/${id}/context`),
    decide: (id: string, body: DecisionBody) =>
      client.post<{ moderation_case_id: string | null; performed: Record<string, unknown> }>(
        `${BASE}/reports/${id}/decision`,
        body,
        newIdempotencyKey(),
      ),
    assign: (id: string, assigneeUserId: string | null) =>
      client.post<{ report: { id: string; assigned_to_user_id: string | null } }>(
        `${BASE}/reports/${id}/assign`,
        { assignee_user_id: assigneeUserId },
      ),
    assignees: () => client.get<{ assignees: Person[] }>(`${BASE}/assignees`),
    communityRemovals: (params: { club_id?: string; removed_by?: string; limit?: number; offset?: number }) =>
      client.get<{ items: CommunityRemoval[]; total: number; limit: number; offset: number }>(
        `${BASE}/community-removals${qs(params)}`,
      ),
    fileReport: (body: { subject_type: string; subject_id: string; reason_code: string; note: string }) =>
      client.post<{ report: { id: string } }>(`${BASE}/reports`, body, newIdempotencyKey()),
    listTakedowns: (params: { view?: string; subject_type?: string; limit?: number; offset?: number }) =>
      client.get<{ takedowns: Takedown[]; total: number }>(`${BASE}/takedowns${qs(params)}`),
    restore: (id: string, reason: string) =>
      client.post(`${BASE}/takedowns/${id}/restore`, { reason }, newIdempotencyKey()),
    purgeMedia: (id: string, reason: string) =>
      client.post<{ objects_queued: number }>(`${BASE}/takedowns/${id}/purge-media`, { reason }, newIdempotencyKey()),
    userHistory: (userId: string) =>
      client.get<{ user: Person; history: UserHistory; appeals: Array<Record<string, unknown>> }>(
        `${BASE}/users/${userId}/history`,
      ),
    blocklist: () => client.get<{ terms: BlocklistTerm[] }>(`${BASE}/blocklist`),
    addTerm: (body: { term: string; match_type: string; surfaces: string[]; priority: string; note?: string }) =>
      client.post(`${BASE}/blocklist`, body, newIdempotencyKey()),
    updateTerm: (id: string, body: { is_active?: boolean; priority?: string }) =>
      client.patch(`${BASE}/blocklist/${id}`, body, newIdempotencyKey()),
    deleteTerm: (id: string) =>
      client.request(`${BASE}/blocklist/${id}`, { method: "DELETE", idempotencyKey: newIdempotencyKey() }),
  };
}

export type DeskApi = ReturnType<typeof createDeskApi>;

export const REASONS: Array<{ value: string; label: string }> = [
  { value: "spam", label: "Spam or misleading promotion" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "hate", label: "Hate or discrimination" },
  { value: "sexual_content", label: "Nudity or sexual content" },
  { value: "violence", label: "Violence or threats" },
  { value: "self_harm", label: "Self-harm" },
  { value: "scam", label: "Scam or fraud" },
  { value: "impersonation", label: "Impersonation" },
  { value: "illegal", label: "Illegal activity" },
  { value: "ip", label: "Copyright or intellectual property" },
  { value: "minor_safety", label: "Safety of a minor" },
  { value: "misinformation", label: "False or misleading information" },
  { value: "other", label: "Something else" },
  { value: "blocklist", label: "Flagged by an automatic filter" },
];

export const SUBJECT_LABEL: Record<string, string> = {
  post: "Post",
  comment: "Comment",
  video: "Highlight",
  replay: "Replay",
  chat_message: "Chat message",
  user: "Player",
  community: "Community",
  business: "Business",
  booking: "Booking",
  review: "Review",
};
